import { SessionProfile } from '../routes/manifest';

export const SESSION_UNAUTHORIZED_EVENT = 'session:unauthorized';

let sessionCache: SessionProfile | null = null;
let sessionPromise: Promise<SessionProfile | null> | null = null;

export class SessionFetchError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message);
    this.name = 'SessionFetchError';
    if (options && 'cause' in options) {
      (this as Error & { cause?: unknown }).cause = options.cause;
    }
  }
}

export async function getSession(options?: { force?: boolean }): Promise<SessionProfile | null> {
  const force = options?.force ?? false;
  const previousCache = sessionCache;
  if (force) {
    sessionPromise = null;
  }

  if (!force && sessionCache) {
    return sessionCache;
  }

  if (!sessionPromise) {
    sessionPromise = fetchSession();
  }

  try {
    const session = await sessionPromise;
    sessionCache = session;
    return session;
  } catch (error) {
    sessionCache = previousCache;
    throw error;
  } finally {
    sessionPromise = null;
  }
}

async function fetchSession(): Promise<SessionProfile | null> {
  try {
    const response = await fetch('/api/v1/auth/session', {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Request-ID': generateRequestId()
      }
    });

    if (response.status === 401) {
      // Not authenticated
      return null;
    }

    if (!response.ok) {
      throw new SessionFetchError(`Session fetch failed: ${response.status}`);
    }

    const sessionData = await response.json();
    
    // Ensure roles and scopes are arrays to prevent null/undefined errors
    // Map 'id' field from API to 'userId' expected by frontend
    const session: SessionProfile = {
      ...sessionData,
      userId: sessionData.id || sessionData.userId,
      roles: sessionData.roles || [],
      scopes: sessionData.scopes || [],
      navTree: sessionData.navTree || []
    };
    return session;
  } catch (error) {
    console.error('Failed to fetch session:', error);
    if (error instanceof SessionFetchError) {
      throw error;
    }
    throw new SessionFetchError('Failed to fetch session', { cause: error });
  }
}

export function clearSession(): void {
  sessionCache = null;
  sessionPromise = null;
}

export function notifySessionExpired(): void {
  clearSession();
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(SESSION_UNAUTHORIZED_EVENT));
  }
}

export async function login(returnUrl?: string, realm?: string): Promise<void> {
  const params = new URLSearchParams();
  if (returnUrl) {
    params.set('return_url', returnUrl);
  }
  if (realm) {
    params.set('realm', realm);
  }

  window.location.href = `/api/v1/auth/login?${params.toString()}`;
}

export async function logout(): Promise<void> {
  clearSession();
  const params = new URLSearchParams({ return_url: '/?signed_out=1' });
  window.location.assign(`/api/v1/auth/logout?${params.toString()}`);
}

function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}
